import axios from "axios";

import api from '../../../api/axiosInstance'

const INACTIVITY_RESUME_WINDOW_MS = 1 * 60 * 60 * 1000;
const SYSTEM_SESSION_ID_KEY = 'systemSessionId';
import { displayToast } from '../toast/actions';
import { UserRole } from '../../../helpers/roles';
import { parseAuth0Error } from '../../../helpers/functions';

/**
 * Normalizes error messages for display
 * If error message is empty or whitespace, returns session ended message
 * @param {string} errorMessage - The error message to normalize
 * @returns {string} Normalized error message
 */
export const normalizeErrorMessage = (errorMessage) => {
  if (!errorMessage || typeof errorMessage !== 'string' || errorMessage.trim() === '') {
    return 'Session has ended.';
  }
  return errorMessage;
};

/**
 * Fetches Zendesk Help Center categories, sections, and articles in one call.
 * Used by ZendeskGuidedEntry to power FAQ categories and list (articles mapped to categories via sections).
 * Stores result in Redux as zendeskHelpCenterData: { categories, sections, articles }.
 * @returns {Promise<{ categories: Array, sections: Array, articles: Array }>}
 */
export const getZendeskHelpCenterArticles = () => {
  return async (dispatch) => {
    const accessToken = sessionStorage.getItem('accessToken');
    const blockedRoutes = [
      '/login',
      '/forgot-password',
      '/forgot-username',
      '/forgotten-user-id',
      '/show-username',
      '/check-email',
      '/confirmed-new-link',
      '/confirmed-new-invite',
      '/change-password',
      '/mfa-verification',
      '/password-changed-success',
      '/portal/selection',
    ];
    const currentPath = typeof window !== 'undefined' ? window.location.pathname.replace(/\/$/, '') : '';

    // Defensive guard: this action should run only post-login and never on auth/portal routes.
    if (!accessToken || blockedRoutes.includes(currentPath)) {
      return null;
    }

    dispatch({ type: "SET_ZENDESK_HELP_CENTER_LOADING", payload: true });
    dispatch({ type: "CLEAR_ZENDESK_HELP_CENTER_ERROR" });

    try {
      const zendeskHelpCenterUrl = import.meta.env.VITE_ZENDESK_HELP_CENTER_ARTICLES_URL;
      const zendeskApiUsername = import.meta.env.VITE_ZENDESK_API_USERNAME;
      const zendeskApiToken = import.meta.env.VITE_ZENDESK_API_TOKEN;

      if (!zendeskHelpCenterUrl) {
        throw new Error('Zendesk Help Center URL is missing. Set VITE_ZENDESK_HELP_CENTER_ARTICLES_URL in your .env file.');
      }

      if (!zendeskApiUsername || !zendeskApiToken) {
        throw new Error('Zendesk credentials are missing. Set VITE_ZENDESK_API_USERNAME and VITE_ZENDESK_API_TOKEN in your .env file.');
      }

      const basicAuthValue = `Basic ${btoa(`${zendeskApiUsername}:${zendeskApiToken}`)}`;
      const headers = {
        Authorization: basicAuthValue,
        'Content-Type': 'application/json',
      };

      const baseUrl = zendeskHelpCenterUrl.replace('/api/v2/help_center/articles', '');
      const categoriesUrl = `${baseUrl}/api/v2/help_center/categories.json`;
      const sectionsUrl = `${baseUrl}/api/v2/help_center/sections.json`;

      const [categoriesRes, sectionsRes, articlesRes] = await Promise.all([
        axios.get(categoriesUrl, { headers }),
        axios.get(sectionsUrl, { headers }),
        axios.get(zendeskHelpCenterUrl, { params: { per_page: 100 }, headers }),
      ]);

      const payload = {
        categories: categoriesRes.data?.categories ?? [],
        sections: sectionsRes.data?.sections ?? [],
        articles: articlesRes.data?.articles ?? [],
      };

      dispatch({
        type: "GET_ZENDESK_HELP_CENTER_ARTICLES_SUCCESS",
        payload,
      });

      return payload;
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.message || 'Failed to load Zendesk Help Center data.';

      dispatch({
        type: "GET_ZENDESK_HELP_CENTER_ARTICLES_FAILURE",
        payload: errorMessage,
      });
      dispatch(displayToast(normalizeErrorMessage(errorMessage), 'error', { autoClose: false }));
      throw error;
    } finally {
      dispatch({ type: "SET_ZENDESK_HELP_CENTER_LOADING", payload: false });
    }
  };
};

export const getUsers = () => {
  return async (dispatch) => {
    try {
      // Make API call to fetch users
      const response = await api.get('/user');
      const data = response.data;

      dispatch({ type: 'GET_USERS_SUCCESS', payload: data });
    } catch (error) {
      dispatch({ type: 'GET_USERS_FAILURE', payload: error.message });
    }
  };
};

/**
 * Finds username (email) by email, driving license, and phone number
 */
export const forgotUsername = ({ email, drivingLicense, phoneNumber }, navigate) => {
  return async (dispatch) => {
    try {
      dispatch({ type: "SET_FORGOT_USERNAME_LOADING", payload: true });

      // Use separate axios without interceptors
      const fxApi = axios.create({
        baseURL: import.meta.env.VITE_APP_API_URL || 'http://localhost:62805',
      });

      const response = await fxApi.post('/auth/forgot-username', {
        email,
        drivingLicense,
        phoneNumber,
      });

      if (response.status === 200) {
        const email = response.data?.email || response.data?.Email;
        // Navigate to check-email page with isForgotUsername=true parameter (no toast message)
        if (navigate) {
          navigate('/check-email?isForgotUsername=true', { state: { email } });
        }
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'We could not find an account matching those details.';
      dispatch(displayToast(normalizeErrorMessage(errorMessage), 'error', { autoClose: false }));
    } finally {
      dispatch({ type: "SET_FORGOT_USERNAME_LOADING", payload: false });
    }
  };
};


export const getUserById = (id) => {
  return async (dispatch) => {
    try {
      // Make API call to fetch a single user
      const response = await api.get(`/user/${id}`);
      const data = response.data;

      dispatch({ type: 'GET_USER_SUCCESS', payload: data });
    } catch (error) {
      dispatch({ type: 'GET_USER_FAILURE', payload: error.message });
    }
  };
};

export const getReelMemberById = (id) => {
  return async (dispatch) => {
    try {
      dispatch(setLoading(true));
      try {
        const response = await api.get(`/reel-member/${id}`);
        dispatch({
          type: "GET_REELMEMBER_BY_ID_SUCCESS",
          payload: response.data,
        });
      } catch (error) {
        dispatch(displayToast('Failed to fetch data for REEL Member.', 'error', { autoClose: false }));
      }
    } catch (error) {
      dispatch({ type: "GET_REELMEMBER_BY_ID_FAILURE", payload: error.message });
    }
  };
}

export const setLoading = (isLoading) => {
  return async (dispatch) => {
    dispatch({ type: "SET_LOADING", payload: isLoading });
  };
};

export const setCurrentUserRoles = (userRoles) => {
  return async (dispatch) => {
    // Update both legacy and new user roles properties for consistency
    dispatch({ type: "SET_CURRENT_USER_ROLES", payload: userRoles });
    dispatch({ type: "GET_USER_ROLES_SUCCESS", payload: userRoles });

    // Also persist to sessionStorage
    if (userRoles && userRoles.length > 0) {
      sessionStorage.setItem('userRoles', JSON.stringify(userRoles));
    }
  };
};

export const getCurrentUserDetails = (userId, showLoader = true) => {
  return async (dispatch) => {
    try {
      if (showLoader) {
        dispatch({ type: "SET_USER_DETAILS_LOADING", payload: true });
      }
      try {
        const response = await api.get(`/user/${userId}`);
        dispatch({
          type: "GET_CURRENT_USER_DETAILS_SUCCESS",
          payload: response.data,
        });
      } catch (error) {
        dispatch(displayToast('Failed to fetch user details.', 'error', { autoClose: false }));
      }
    } catch (error) {
      dispatch({ type: "GET_CURRENT_USER_DETAILS_FAILURE", payload: error.message });
    } finally {
      dispatch({ type: "SET_USER_DETAILS_LOADING", payload: false });
    }
  };
};

export const updateLegalContent = (userId, isTermAccepted, isPrivacyAccepted) => {
  return async (dispatch) => {
    try {
      dispatch({ type: "SET_UPDATE_LEGAL_CONTENT_LOADING", payload: true });
      try {
        await api.put(`/user/${userId}/legal-detail?isTermOfUseAccepted=${isTermAccepted}&isPrivacyPolicyAccepted=${isPrivacyAccepted}`);
        dispatch(getCurrentUserDetails(userId, false));
      } catch (error) {
        dispatch(displayToast('Failed to fetch user details.', 'error', { autoClose: false }));
      }
    } catch (error) {
      dispatch({ type: "GET_CURRENT_USER_DETAILS_FAILURE", payload: error.message });
    } finally {
      dispatch({ type: "SET_UPDATE_LEGAL_CONTENT_LOADING", payload: false });
    }
  };
};

export const updateReelMember = (id, reelMember) => {
  return async (dispatch) => {
    try {
      dispatch({ type: "SET_REELMEMBER_SAVE_LOADING", payload: true });
      try {
        await api.put(`/reel-member/${id}`, reelMember);
        dispatch(displayToast('REEL Member has been updated successfully.', 'success', { autoClose: 4000 }));
        await dispatch(getCurrentUserDetails(id, false));
        // Refresh the current member details
        await dispatch(getReelMemberById(id));
      } catch (error) {
        dispatch(displayToast('Failed to update REEL Member details.', 'error', { autoClose: false }));
      }
    } catch (error) {
      dispatch({ type: "UPDATE_REELMEMBER_FAILURE", payload: error.message });
    } finally {
      dispatch({ type: "SET_REELMEMBER_SAVE_LOADING", payload: false });
    }
  };
};

export const setUserDetailsLoading = (value) => {
  return async (dispatch) => {
    dispatch({ type: "SET_USER_DETAILS_LOADING", payload: value });
  };
}

let permissionsRequestInFlight = null;
let companyRolesRequestInFlight = null;
let rolesRequestInFlight = null;
let permissionsLoadedOnce = false;
let companyRolesLoadedOnce = false;
let rolesLoadedOnce = false;

const hasSliceData = (sliceState, key) => {
  const value = sliceState?.[key];
  return Array.isArray(value) && value.length > 0;
};

const shouldSkipMetadataFetch = (state, key, loadedOnce, forceRefresh = false) => {
  if (forceRefresh) {
    return false;
  }

  const scopedSlice = state?.[key];
  const usersSlice = state?.users;
  return loadedOnce || hasSliceData(scopedSlice, key) || hasSliceData(usersSlice, key);
};


export const getPermissions = ({ forceRefresh = false } = {}) => {
  return async (dispatch, getState) => {
    if (shouldSkipMetadataFetch(getState(), 'permissions', permissionsLoadedOnce, forceRefresh)) {
      return;
    }
    if (!forceRefresh && permissionsRequestInFlight) {
      return permissionsRequestInFlight;
    }

    try {
      permissionsRequestInFlight = (async () => {
        const response = await api.get(`/permissions`);
        dispatch({
          type: "GET_PERMISSIONS_SUCCESS",
          payload: response.data,
        });
        permissionsLoadedOnce = true;
      })();

      await permissionsRequestInFlight;
    } catch (error) {
      try {
        dispatch(displayToast('Failed to fetch data for Permissions.', 'error', { autoClose: false }));
      } catch (toastError) {
        // Keep behaviour consistent even if toast dispatch fails.
      }
      dispatch({ type: "GET_PERMISSIONS_FAILURE", payload: error.message });
    } finally {
      permissionsRequestInFlight = null;
    }
  };
}

export const getCompanyRoles = ({ forceRefresh = false } = {}) => {
  return async (dispatch, getState) => {
    if (shouldSkipMetadataFetch(getState(), 'companyRoles', companyRolesLoadedOnce, forceRefresh)) {
      return;
    }
    if (!forceRefresh && companyRolesRequestInFlight) {
      return companyRolesRequestInFlight;
    }

    try {
      companyRolesRequestInFlight = (async () => {
        const response = await api.get(`/company-roles`);
        dispatch({
          type: "GET_COMPANY_ROLES_SUCCESS",
          payload: response.data,
        });
        companyRolesLoadedOnce = true;
      })();

      await companyRolesRequestInFlight;
    } catch (error) {
      try {
        dispatch(displayToast('Failed to fetch data for Company roles.', 'error', { autoClose: false }));
      } catch (toastError) {
        // Keep behaviour consistent even if toast dispatch fails.
      }
      dispatch({ type: "GET_COMPANY_ROLES_FAILURE", payload: error.message });
    } finally {
      companyRolesRequestInFlight = null;
    }
  };
}

export const getRoles = ({ forceRefresh = false } = {}) => {
  return async (dispatch, getState) => {
    if (shouldSkipMetadataFetch(getState(), 'roles', rolesLoadedOnce, forceRefresh)) {
      return;
    }
    if (!forceRefresh && rolesRequestInFlight) {
      return rolesRequestInFlight;
    }

    try {
      rolesRequestInFlight = (async () => {
        const response = await api.get(`/roles`);
        dispatch({
          type: "GET_ROLES_SUCCESS",
          payload: response.data,
        });
        rolesLoadedOnce = true;
      })();

      await rolesRequestInFlight;
    } catch (error) {
      try {
        dispatch(displayToast('Failed to fetch data for Roles.', 'error', { autoClose: false }));
      } catch (toastError) {
        // Keep behaviour consistent even if toast dispatch fails.
      }
      dispatch({ type: "GET_ROLES_FAILURE", payload: error.message });
    } finally {
      rolesRequestInFlight = null;
    }
  };
}

export const login = (username, password, navigate) => {
  return async (dispatch) => {
    try {
      dispatch({ type: "SET_LOGIN_LOADING", payload: true });
      dispatch({ type: "CLEAR_LOGIN_ERROR" });
      dispatch({ type: "CLEAR_ACCOUNT_LOCKED" });

      // Create a separate axios instance for login without Auth0 interceptor
      const loginApi = axios.create({
        baseURL: import.meta.env.VITE_APP_API_URL || 'http://localhost:62805',
      });

      // Backend LoginEndpoint uses absolute route "/login"
      const response = await loginApi.post('/login', { username, password });

      // Normalize response to support both legacy and new DTO shapes
      const raw = response.data || {};
      const start = {
        requiresMfa: raw.requiresMfa ?? raw.RequiresMfa ?? false,
        // Ensure first-time setup gets mapped regardless of casing or alternate field names
        enrollmentRequired: (
          raw.enrollmentRequired ??
          raw.isFirstTimeSetup ??
          raw.IsFirstTimeSetup ??
          raw.requiresMfaSetup ??
          false
        ),
        mfaToken: raw.mfaToken ?? raw.sessionToken ?? raw.SessionToken ?? null,
        userId: raw.userId ?? raw.UserId ?? null,
        barcodeUri: raw.barcodeUri ?? raw.qrCodeImage ?? raw.QrCodeImage ?? (raw.qrCodeImageBase64 ? `data:image/png;base64,${raw.qrCodeImageBase64}` : null),
        secret: raw.secret ?? raw.SecretKey ?? null,
        accessToken: raw.accessToken ?? raw.AccessToken ?? null,
        sessionId: raw.sessionId ?? raw.SessionId ?? null,
      };

      if (start.sessionId) {
        sessionStorage.setItem(SYSTEM_SESSION_ID_KEY, start.sessionId);
      }

      if (start.requiresMfa) {
        // Map to existing Redux state fields
        dispatch({
          type: "LOGIN_MFA_REQUIRED",
          payload: {
            sessionToken: start.mfaToken || null,
            userId: start.userId || null,
            sessionId: start.sessionId || null,
            isFirstTimeSetup: !!start.enrollmentRequired,
            qrCodeImage: start.barcodeUri
              ? (start.barcodeUri.startsWith('data:') ? start.barcodeUri : `data:image/png;base64,${start.barcodeUri}`)
              : null,
            secretKey: start.secret || null,
          },
        });
      } else {
        // No MFA required - login complete
        const userInfo = { userId: null };

        if (start.accessToken) {
          sessionStorage.setItem('accessToken', start.accessToken);
          sessionStorage.setItem('userInfo', JSON.stringify(userInfo));
          
          // Reset inactivity timer after successful login
          sessionStorage.setItem('lastActivityTimestamp', Date.now().toString());
        }
        dispatch({
          type: "LOGIN_SUCCESS",
          payload: { userInfo, accessToken: start.accessToken },
        });

        // Inactivity re-login behavior:
        // - <= 1 hour: auto-resume (no popup)
        // - > 1 hour and <= 4 hours: navigate back first, then show popup on that page
        // - > 4 hours: treat as normal login
        const inactivityLogoutTimestamp = sessionStorage.getItem('inactivityLogoutTimestamp');
        const inactivityHxReturnUrl = sessionStorage.getItem('hxReturnUrl');
        if (inactivityLogoutTimestamp && inactivityHxReturnUrl) {
          const logoutTime = parseInt(inactivityLogoutTimestamp, 10);
          const timeSinceLogout = Date.now() - logoutTime;
          const POPUP_WINDOW_MAX_MS = 4 * 60 * 60 * 1000;

          if (timeSinceLogout <= INACTIVITY_RESUME_WINDOW_MS) {
            const returnUrl = getValidReturnUrl();
            if (returnUrl) {
              navigate(returnUrl);
              return;
            }
          } else if (timeSinceLogout <= POPUP_WINDOW_MAX_MS) {
            sessionStorage.removeItem('inactivityLogoutTimestamp');
            sessionStorage.removeItem('hxReturnUrl');
            sessionStorage.setItem('inactivityReturnUrl', inactivityHxReturnUrl);
            sessionStorage.setItem('showInactivityReturnModal', 'true');
            navigate(inactivityHxReturnUrl);
            return;
          } else {
            // Expired beyond 4 hours - clear and fall through to normal navigation.
            sessionStorage.removeItem('inactivityLogoutTimestamp');
            sessionStorage.removeItem('hxReturnUrl');
          }
        }

        // Default navigation
        sessionStorage.setItem('triggerCompliance', 'true');
        navigate('/patients');
      }
    } catch (error) {
      if (error.response?.status === 429) {
        dispatch({
          type: "LOGIN_ACCOUNT_LOCKED",
          payload: {
            message: error.response?.data?.message || 'Too many attempts, please try after 15 minutes.',
            loginFailCount: error.response?.data?.loginFailCount,
            loginFailedAt: error.response?.data?.loginFailedAt,
          }
        });
      } else {
        const errorMessage = error.response?.data?.message || 'Login failed. Please check your credentials.';
        dispatch({
          type: "LOGIN_FAILURE",
          payload: errorMessage
        });
      }
    } finally {
      dispatch({ type: "SET_LOGIN_LOADING", payload: false });
    }
  };
};

/**
 * Checks if there's a valid inactivity return URL without clearing it
 * This is used to determine if we should show the modal
 * @returns {boolean} True if valid return URL exists, false otherwise
 */
const checkValidInactivityReturn = () => {
  const logoutTimestamp = sessionStorage.getItem('inactivityLogoutTimestamp');
  const returnUrl = sessionStorage.getItem('hxReturnUrl');
  
  // If no logout timestamp or return URL, no return needed
  if (!logoutTimestamp || !returnUrl) {
    return false;
  }
  
  // Check if logout was within the 1-hour resume window
  const logoutTime = parseInt(logoutTimestamp, 10);
  const currentTime = Date.now();
  const timeSinceLogout = currentTime - logoutTime;

  return timeSinceLogout <= INACTIVITY_RESUME_WINDOW_MS;
};

/**
 * Checks if user should be redirected to saved return URL after inactivity logout
 * Returns the return URL if logout was within 1 hour, otherwise null
 * @returns {string|null} Return URL if valid, null otherwise
 */
const getValidReturnUrl = () => {
  const logoutTimestamp = sessionStorage.getItem('inactivityLogoutTimestamp');
  const returnUrl = sessionStorage.getItem('hxReturnUrl');
  
  // If no logout timestamp or return URL, no redirect needed
  if (!logoutTimestamp || !returnUrl) {
    return null;
  }

  // Check if logout was within the 1-hour resume window
  const logoutTime = parseInt(logoutTimestamp, 10);
  const currentTime = Date.now();
  const timeSinceLogout = currentTime - logoutTime;

  if (timeSinceLogout <= INACTIVITY_RESUME_WINDOW_MS) {
    // Clear the stored values after reading
    sessionStorage.removeItem('inactivityLogoutTimestamp');
    sessionStorage.removeItem('hxReturnUrl');
    return returnUrl;
  } else {
    // Clear expired values
    sessionStorage.removeItem('inactivityLogoutTimestamp');
    sessionStorage.removeItem('hxReturnUrl');
    return null;
  }
};

/**
 * Verifies MFA code to complete login process and handles post-login navigation
 * @param {string} sessionToken - Session token from login response
 * @param {string} mfaCode - 6-digit MFA code entered by user
 * @param {function} navigate - Navigation function
 */
export const verifyMfa = (sessionToken, mfaCode, navigate) => {
  return async (dispatch, getState) => {
    try {
      dispatch({ type: "SET_MFA_VERIFY_LOADING", payload: true });
      dispatch({ type: "CLEAR_LOGIN_ERROR" });
      const state = getState();

      // Create a separate axios instance for MFA verification without Auth0 interceptor
      const mfaApi = axios.create({
        baseURL: import.meta.env.VITE_APP_API_URL || 'http://localhost:62805',
      });

      // Backend VerifyMfa endpoint expects { sessionToken, mfaCode, isFirstTime?, userId? }
      const isFirstTime = !!state.users?.isFirstTimeSetup;
      const verifyPayload = {
        sessionToken: sessionToken,
        mfaCode: mfaCode,
        sessionId: sessionStorage.getItem(SYSTEM_SESSION_ID_KEY),
        ...(isFirstTime ? { isFirstTime: true, userId: state.users?.userId } : {})
      };
      const response = await mfaApi.post('/verify-mfa', verifyPayload);
      // Response is TokensResponse
      if (response.status === 200) {
        const userInfo = {
          userId: state.users.userId,
        };

        // Store access token in sessionStorage
        if (response.data.accessToken) {
          sessionStorage.setItem('accessToken', response.data.accessToken);
          sessionStorage.setItem('userInfo', JSON.stringify(userInfo));
          if (response.data.sessionId) {
            sessionStorage.setItem(SYSTEM_SESSION_ID_KEY, response.data.sessionId);
          }

          // --- start ---
          // Reset inactivity timer after successful MFA validation
          sessionStorage.setItem('lastActivityTimestamp', Date.now().toString());
          // --- end ---
        }

        dispatch({
          type: "MFA_VERIFY_SUCCESS",
          payload: {
            userInfo,
            accessToken: response.data.accessToken
          }
        });

        // Fetch user details after successful MFA verification
        await dispatch(getCurrentUserDetails(userInfo.userId));
        // Fetch user roles after successful MFA verification
        await dispatch(getUserRoles(userInfo.userId));

        // Get user details from Redux state after fetching
        const currentState = getState();
        const currentLoggedInUserDetail = currentState.users.currentLoggedInUserDetail;

        const diffInDays = calculatePasswordDaysReminder(currentLoggedInUserDetail?.passwordLastChangedOn);
        dispatch({
          type: "SET_PASSWORD_CHANGE_REMINDER",
          payload: diffInDays,
        });

        // Wait a bit for the state to update and then get the current state
        await new Promise(resolve => setTimeout(resolve, 100));

        // - <= 1 hour: auto-resume (no popup)
        // - > 1 hour and <= 4 hours: navigate back first, then show popup on that page
        // - > 4 hours: continue normal role-based navigation
        const inactivityLogoutTimestamp = sessionStorage.getItem('inactivityLogoutTimestamp');
        const inactivityHxReturnUrl = sessionStorage.getItem('hxReturnUrl');
        if (inactivityLogoutTimestamp && inactivityHxReturnUrl) {
          const logoutTime = parseInt(inactivityLogoutTimestamp, 10);
          const timeSinceLogout = Date.now() - logoutTime;
          const POPUP_WINDOW_MAX_MS = 4 * 60 * 60 * 1000;

          if (timeSinceLogout <= INACTIVITY_RESUME_WINDOW_MS) {
            const returnUrl = getValidReturnUrl();
            if (returnUrl) {
              navigate(returnUrl);
              return;
            }
          } else if (timeSinceLogout <= POPUP_WINDOW_MAX_MS) {
            sessionStorage.removeItem('inactivityLogoutTimestamp');
            sessionStorage.removeItem('hxReturnUrl');
            sessionStorage.setItem('inactivityReturnUrl', inactivityHxReturnUrl);
            sessionStorage.setItem('showInactivityReturnModal', 'true');
            navigate(inactivityHxReturnUrl);
            return;
          } else {
            sessionStorage.removeItem('inactivityLogoutTimestamp');
            sessionStorage.removeItem('hxReturnUrl');
          }
        }

        // Use the centralized navigation function with userRoles from Redux store
        // No need to make another API call since we already have userRoles
        try {
          // Get userRoles from the Redux store after dispatching getUserRoles
          const state = getState();
          const userRoles = state.users.userRoles;
          const userDetail = state.users.currentLoggedInUserDetail;

          // Since we've already dispatched getUserRoles, we can use the userRoles
          // that are now available in the Redux store for navigation
          // The navigation will be handled by the App.jsx useEffect that watches for userRoles changes
          handleRoleBasedNavigation(userRoles, navigate, userDetail.acceptedPrivacyPolicy, userDetail.acceptedTermOfUse, userDetail);
        } catch (error) {
          // Fallback navigation if user roles couldn't be loaded
          navigate("/dashboard");
        }
      } else {
        dispatch({
          type: "MFA_VERIFY_FAILURE",
          payload: response.data.message
        });
      }
    } catch (error) {
      dispatch({
        type: "MFA_VERIFY_FAILURE",
        payload: error?.response?.data?.message || "Invalid MFA code. Please try again."
      });
    } finally {
      dispatch({ type: "SET_MFA_VERIFY_LOADING", payload: false });
    }
  };
};


/**
 * Clears login error and resets MFA state
 */
export const clearLoginError = () => {
  return async (dispatch) => {
    dispatch({ type: "CLEAR_LOGIN_ERROR" });
  };
};

/**
 * Logs out user and clears authentication state
 */
export const logout = (logoutType = 'UserLogout') => {
  return async (dispatch) => {
    const sessionId = sessionStorage.getItem(SYSTEM_SESSION_ID_KEY);
    const accessToken = sessionStorage.getItem('accessToken');
    const resolvedLogoutType = sessionStorage.getItem('systemLogoutReason') || logoutType;

    if (sessionId && accessToken) {
      try {
        await api.post('/auth/logout', { sessionId, logoutType: resolvedLogoutType });
      } catch (error) {
        // Best-effort logging call should not block local logout cleanup.
      }
    }

    // Mark user as logged out today (for welcome back logic)
    localStorage.setItem('wasLoggedOutToday', 'true');

    // Clear session storage
    sessionStorage.removeItem('accessToken');
    sessionStorage.removeItem('userInfo');
    sessionStorage.removeItem('userRoles');
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('lastClickedElement');
    sessionStorage.removeItem('lastActivityTimestamp');
    sessionStorage.removeItem(SYSTEM_SESSION_ID_KEY);
    sessionStorage.removeItem('systemLogoutReason');
    // Reset the "shown this session" flag so the password reminder appears on the next login.
    sessionStorage.removeItem('passwordReminderShownThisSession');
    // Clear compliance flags so they don't carry over into the next login session.
    sessionStorage.removeItem('hasAcceptedCompliance');
    sessionStorage.removeItem('triggerCompliance');
    sessionStorage.removeItem('triggerComplianceOnLegalPage');
    sessionStorage.removeItem('isFirstTimeLegalFlow');
    // Reset login state
    dispatch({ type: "LOGOUT" });
  };
};

export const forgotPassword = (email, navigate, showSuccessToast = false) => {
  return async (dispatch) => {
    try {
      dispatch({ type: "SET_FORGOT_PASSWORD_LOADING", payload: true });

      // Create a separate axios instance for forgot password without Auth0 interceptor
      const forgotPasswordApi = axios.create({
        baseURL: import.meta.env.VITE_APP_API_URL || 'http://localhost:62805',
      });

      const baseUrl = window.location.origin;
      const response = await forgotPasswordApi.post('/auth/reset-password', {
        email,
        baseUrl
      });
      if (response.status === 200) {
        if (showSuccessToast) {
          dispatch(
            displayToast(
              'If your account exists, a reset link has been sent. Check your email.',
              'success',
              { autoClose: 4000 }
            )
          );
        }
        // Navigate to check-email page with email in state
        navigate('/check-email', { state: { email } });
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to send password reset email. Please try again.';
      dispatch(displayToast(normalizeErrorMessage(errorMessage), 'error', { autoClose: false }));
    } finally {
      dispatch({ type: "SET_FORGOT_PASSWORD_LOADING", payload: false });
    }
  };
};

export const resendPasswordResetEmail = (email, onSuccess, isNewUser = false, isLinkExpired = false) => {
  return async (dispatch) => {
    try {
      dispatch({ type: "SET_RESEND_EMAIL_LOADING", payload: true });

      // Create a separate axios instance for resend email without Auth0 interceptor
      const resendEmailApi = axios.create({
        baseURL: import.meta.env.VITE_APP_API_URL || 'http://localhost:62805',
      });

      const baseUrl = window.location.origin;
      const response = await resendEmailApi.post('/auth/reset-password', {
        email,
        baseUrl,
        isNewUser,
        isLinkExpired
      });

      if (response.status === 200) {
        const msg = isNewUser ? 'Invitation link sent successfully.' : 'Password reset email resent successfully.';
        dispatch(displayToast(msg, 'success', { autoClose: 4000 }));
        if (typeof onSuccess === 'function') {
          try {
            onSuccess();
          } catch (e) {
            // no-op
          }
        }
      }
    } catch (error) {
      const errorMessage = "Failed to resend password reset email. Please try again.";
      dispatch(displayToast(normalizeErrorMessage(errorMessage), 'error', { autoClose: false }));
    } finally {
      dispatch({ type: "SET_RESEND_EMAIL_LOADING", payload: false });
    }
  };
};

export const changePasswordWithToken = (token, newPassword, navigate, successPath) => {
  return async (dispatch) => {
    try {
      dispatch({ type: "SET_CHANGE_PASSWORD_LOADING", payload: true });

      // Create a separate axios instance for change password without Auth0 interceptor
      const changePasswordApi = axios.create({
        baseURL: import.meta.env.VITE_APP_API_URL || 'http://localhost:62805',
      });

      const response = await changePasswordApi.post('/auth/change-password', {
        token,
        newPassword
      });
      // Password change successful
      if (response.status === 200) {
        dispatch(displayToast('Password changed successfully. You can now login with your new password.', 'success', { autoClose: 4000 }));
        navigate(successPath || '/password-changed-success');
      }
    } catch (error) {
      // Extract error message from various possible error structures
      const errorMessage = error?.response?.data?.message || 
                          (typeof error?.response?.data === 'string' ? error.response.data : error?.response?.data?.error) ||
                          error?.message || 
                          'Failed to change password. Please try again.';
      
      const errorMessageLower = errorMessage.toLowerCase();
      const isPasswordHistoryError = ['passwordhistoryerror', 'previously used', 'previous', 'history', 'last five']
        .some(keyword => errorMessageLower.includes(keyword));
      
      dispatch({ type: "CHANGE_PASSWORD_FAILURE", payload: errorMessage });
      
      // Don't show toast for password history errors - the popup will handle it
      if (!isPasswordHistoryError) {
        dispatch(displayToast(normalizeErrorMessage(errorMessage), 'error', { autoClose: false }));
      }
    } finally {
      dispatch({ type: "SET_CHANGE_PASSWORD_LOADING", payload: false });
    }
  };
};

/**
 * Fetches user roles from the API
 * @param {string} userId - User ID to fetch roles for
 */
export const getUserRoles = (userId) => {
  return async (dispatch) => {
    try {
      dispatch({ type: "SET_USER_ROLES_LOADING", payload: true });

      const response = await api.get(`/userRoles/${userId}`);
      const userRoles = response.data;

      // Store user roles in sessionStorage for persistence
      sessionStorage.setItem('userRoles', JSON.stringify(userRoles));

      dispatch({
        type: "GET_USER_ROLES_SUCCESS",
        payload: userRoles,
      });

      // Also update currentUserRoles for backward compatibility
      // Extract role names from the structured roles
      const roleNames = userRoles.map(role => role.roleName);
      dispatch({
        type: "SET_CURRENT_USER_ROLES",
        payload: roleNames,
      });
    } catch (error) {
      dispatch(displayToast('Failed to fetch user roles.', 'error', { autoClose: false }));
      dispatch({ type: "GET_USER_ROLES_FAILURE", payload: error.message });
    } finally {
      dispatch({ type: "SET_USER_ROLES_LOADING", payload: false });
    }
  };
};

/**
 * Helper function to extract role names from structured userRoles objects
 * @param {Array} userRoles - Array of role objects from Redux store
 * @returns {Array} Array of role names (strings)
 */
export const extractRoleNames = (userRoles) => {
  if (!userRoles || !Array.isArray(userRoles)) return [];
  return userRoles.map(role => role.roleName);
};

/**
 * Helper function to check if user has a specific role
 * @param {Array} userRoles - Array of role objects from Redux store
 * @param {string} roleName - Role name to check for
 * @returns {boolean} True if user has the specified role
 */
export const hasUserRole = (userRoles, roleName) => {
  if (!userRoles || !Array.isArray(userRoles)) return false;
  return userRoles.some(role => role.roleName === roleName);
};

/**
 * Helper function to get role description for a specific role
 * @param {Array} userRoles - Array of role objects from Redux store
 * @param {string} roleName - Role name to get description for
 * @returns {string|null} Role description or null if not found
 */
export const getRoleDescription = (userRoles, roleName) => {
  if (!userRoles || !Array.isArray(userRoles)) return null;
  const role = userRoles.find(role => role.roleName === roleName);
  return role ? role.roleDescription : null;
};

export const selectUserRoles = (state) => state.users.userRoles;

export const handleRoleBasedNavigation = (userRoles, navigate, acceptedPrivacyPolicy = null, acceptedTermOfUse = null, userDetail = null) => {
  // Use the new userRoles from Redux store instead of userDetails.roles
  // userRoles is now an array of role objects: [{ id, userId, roleId, roleName, roleDescription }]

  // Extract role names for backward compatibility with existing logic
  const roleNames = userRoles?.map(role => role.roleName) || [];

  // If privacy policy and terms of use are not provided, we'll need to get them from user details
  // This allows the function to work with both the new userRoles and legacy userDetails
  let hasAcceptedLegalTerms = acceptedPrivacyPolicy && acceptedTermOfUse;

  // Handle role-based navigation logic
  if (roleNames.length > 0) {
    if (hasAcceptedLegalTerms) {
      const moveDirect = sessionStorage.getItem('moveDirect');
      if (moveDirect) {
        sessionStorage.removeItem('moveDirect');
        navigate("/patients");
        return;
      }
      // Check companyRole flags as source of truth — Auth0 role names may lag behind DB changes
      const companyRole = userDetail?.companyRole;
      const hasAnyAdminAccess = Boolean(
        companyRole?.hasAccessToDevAdmin || companyRole?.hasAccessToStagingAdmin || companyRole?.hasAccessToProdAdmin
      );
      const hasAnyClinicalAccess = Boolean(
        companyRole?.hasAccessToDevClinical || companyRole?.hasAccessToStagingClinical || companyRole?.hasAccessToProdClinical
      );

      // User has accepted legal terms - proceed with role-based navigation
      if (roleNames.includes(UserRole.SuperUser)) {
        // SuperUser role - show pharmacy stores selection
        navigate("/store-selection");
      } else if (
        (roleNames.includes(UserRole.Administration) && roleNames.includes(UserRole.ClinicalService)) ||
        (hasAnyAdminAccess && hasAnyClinicalAccess)
      ) {
        // If the user already accepted compliance in this session (e.g. the request
        // interceptor fired a login redirect mid-popup close), honour that and go
        // straight to patients instead of bouncing them back to portal selection.
        if (sessionStorage.getItem('hasAcceptedCompliance') === 'true') {
          sessionStorage.removeItem('hasAcceptedCompliance');
          sessionStorage.setItem('triggerCompliance', 'true');
          navigate("/patients");
        } else {
          navigate("/portal/selection");
        }
      } else if (roleNames.includes(UserRole.ClinicalService) || hasAnyClinicalAccess) {
        sessionStorage.setItem('triggerCompliance', 'true');
        navigate("/patients");
      } else {
        // Default navigation for other roles
        navigate("/unauthorized");
      }
    } else {
      // User hasn't accepted legal terms - redirect to legal page
      // Mark first-time legal flow so App/MyProfile can chain completion -> compliance popup.
      sessionStorage.setItem('isFirstTimeLegalFlow', 'true');
      navigate("/my-profile?selected=legal");
    }
  } else {
    // Fallback navigation if user roles couldn't be loaded
    navigate("/unauthorized");
  }
};

/**
 * Centralized navigation function based on user roles and legal acceptance
 * This function handles role-based navigation logic that can be reused
 * @param {Object} userDetails - User details from API
 * @param {Function} navigate - Navigation function
 */
const calculatePasswordDaysReminder = (lastChangedOn) => {
  if (!lastChangedOn) return 0;

  const passwordDate = new Date(lastChangedOn);
  if (isNaN(passwordDate.getTime())) return 0;

  const today = new Date();
  const diffInDays = Math.floor((today - passwordDate) / (1000 * 60 * 60 * 24));
  return diffInDays;
};

/**
 * Restores the password change reminder days from the already-loaded user details in Redux.
 * Called during session restore (page reload / app update reload) so the Password Change
 * Reminder popup appears in the correct popup sequence even after a full page refresh.
 */
export const restorePasswordReminder = () => {
  return (dispatch, getState) => {
    const state = getState();
    const passwordLastChangedOn = state.users.currentLoggedInUserDetail?.passwordLastChangedOn;
    const diffInDays = calculatePasswordDaysReminder(passwordLastChangedOn);
    dispatch({ type: "SET_PASSWORD_CHANGE_REMINDER", payload: diffInDays });
  };
};

/**
 * Restores authentication state from sessionStorage
 * This action is called when the app starts to check for existing session
 */
export const restoreAuthFromSession = () => {
  return async (dispatch) => {
    try {
      const accessToken = sessionStorage.getItem('accessToken');
      const userInfoFromStorage = sessionStorage.getItem('userInfo');

      if (accessToken && userInfoFromStorage) {
        try {
          const userInfo = JSON.parse(userInfoFromStorage);

          // Restore authentication state from sessionStorage
          dispatch({
            type: "RESTORE_AUTH_FROM_SESSION",
            payload: {
              userInfo,
              accessToken
            }
          });

          // Restore user roles from sessionStorage
          await dispatch(restoreUserRolesFromSession());

        } catch (error) {
          // Clear invalid sessionStorage data
          sessionStorage.removeItem('accessToken');
          sessionStorage.removeItem('userInfo');
        }
      }
    } catch (error) {
      console.error('Error restoring authentication from session:', error);
    }
  };
};

/**
 * Restores user roles from sessionStorage
 * This action is called when the app starts to check for existing user roles
 */
export const restoreUserRolesFromSession = () => {
  return async (dispatch) => {
    try {
      const userRolesFromStorage = sessionStorage.getItem('userRoles');

      if (userRolesFromStorage) {
        try {
          const userRoles = JSON.parse(userRolesFromStorage);

          // Restore user roles from sessionStorage
          dispatch({
            type: "RESTORE_USER_ROLES_FROM_SESSION",
            payload: userRoles,
          });

          // Also update currentUserRoles for backward compatibility
          const roleNames = userRoles.map(role => role.roleName);
          dispatch({
            type: "SET_CURRENT_USER_ROLES",
            payload: roleNames,
          });

        } catch (error) {
          // Clear invalid sessionStorage data
          sessionStorage.removeItem('userRoles');
        }
      }
    } catch (error) {
      console.error('Error restoring user roles from session:', error);
    }
  };
};

/**
 * Fetches pharmacy stores data from the API
 * This action is called to retrieve pharmacy and store information for SuperUser role
 */
export const getPharmacyStores = () => {
  return async (dispatch) => {
    try {
      // Set loading state
      dispatch({ type: "SET_PHARMACY_STORES_LOADING", payload: true });

      // Clear any previous errors
      dispatch({ type: "CLEAR_PHARMACY_STORES_ERROR" });

      // Make API call to fetch pharmacy stores
      const response = await api.get('/pharmacy/stores');
      const data = response.data;

      // Check if we have valid data regardless of status code
      if (data && Array.isArray(data)) {
        // Dispatch success action with pharmacy stores data
        dispatch({
          type: "GET_PHARMACY_STORES_SUCCESS",
          payload: data
        });
      } else if (data && typeof data === 'object') {
        // Handle case where data might be wrapped in an object
        const dataArray = Array.isArray(data.data) ? data.data :
          Array.isArray(data.result) ? data.result :
            Array.isArray(data.items) ? data.items : null;

        if (dataArray) {
          dispatch({
            type: "GET_PHARMACY_STORES_SUCCESS",
            payload: dataArray
          });
        } else {
          throw new Error('Invalid data format received from API');
        }
      } else {
        throw new Error('Invalid data format received from API');
      }

    } catch (error) {
      // Handle API errors

      // Check if we have a response with data despite the error
      if (error.response?.data) {
        console.log('Error response has data:', error.response.data);
        // If we have data despite the error, try to use it
        if (Array.isArray(error.response.data)) {
          console.log('Using data from error response');
          dispatch({
            type: "GET_PHARMACY_STORES_SUCCESS",
            payload: error.response.data
          });
          return;
        }
      }

      const errorMessage = error.response?.data?.message ||
        error.message ||
        'Failed to fetch pharmacy stores';

      dispatch({
        type: "GET_PHARMACY_STORES_FAILURE",
        payload: errorMessage
      });
    } finally {
      // Clear loading state
      dispatch({ type: "SET_PHARMACY_STORES_LOADING", payload: false });
    }
  };
};

/**
 * Updates user metadata with selected store information
 * This action is called when a user selects a store from the pharmacy stores list
 * @param {string} userId - User ID from currentLoggedInUser
 * @param {string} storeId - Store ID selected by the user
 * @param {Function} navigate - Navigation function to redirect after successful update
 */
export const updateUserMetadata = (userId, storeId, navigate) => {
  return async (dispatch) => {
    try {
      // Set loading state for user metadata update
      dispatch({ type: "SET_USER_METADATA_UPDATE_LOADING", payload: true });

      // Clear any previous errors
      dispatch({ type: "CLEAR_USER_METADATA_UPDATE_ERROR" });

      // Make API call to update user metadata with store selection
      const response = await api.put(`/update-user-metadata?userId=${userId}&storeId=${storeId}`);

      // Check if the API call was successful (status 200)
      if (response.status === 200) {
        // Dispatch success action
        dispatch({
          type: "UPDATE_USER_METADATA_SUCCESS",
          payload: { userId, storeId }
        });

        // Navigate user to patients page on successful update
        if (navigate && typeof navigate === 'function') {
          navigate('/patients');
        }
      } else {
        // Handle non-200 response
        throw new Error(`API returned status ${response.status}`);
      }

    } catch (error) {
      // Handle API errors
      console.error('Error updating user metadata:', error);

      const errorMessage = error.response?.data?.message ||
        error.message ||
        'Failed to update user metadata';

      dispatch({
        type: "UPDATE_USER_METADATA_FAILURE",
        payload: errorMessage
      });

      // Show error toast to user
      dispatch(displayToast(normalizeErrorMessage(errorMessage), 'error', { autoClose: false }));
    } finally {
      // Clear loading state
      dispatch({ type: "SET_USER_METADATA_UPDATE_LOADING", payload: false });
    }
  };
};

/**
 * Updates a user's username
 * Backend expects query params: currentUsername, newUsername
 * [PUT] /user/{userId}/username
 */
export const updateUsername = (userId, currentUsername, newUsername) => {
  return async (dispatch) => {
    try {
      dispatch({ type: "SET_UPDATE_USERNAME_LOADING", payload: true });

      const url = `/user/${encodeURIComponent(userId)}/username?currentUsername=${encodeURIComponent(currentUsername)}&newUsername=${encodeURIComponent(newUsername)}`;
      const response = await api.put(url);

      if (response.status === 204) {
        dispatch({ type: "UPDATE_USERNAME_SUCCESS" });
        // Do not show toast; a modal will be shown by the component and user will be redirected to login
        // Refresh current user details without showing the loader
        dispatch(getCurrentUserDetails(userId, false));
      } else {
        throw new Error(`Unexpected status ${response.status}`);
      }
    } catch (error) {
      const message = error?.response?.data?.message || error?.response?.data || error?.message || 'Failed to update username.';
      dispatch({ type: "UPDATE_USERNAME_FAILURE", payload: message });
      dispatch(displayToast(normalizeErrorMessage(message), 'error', { autoClose: false }));
    } finally {
      dispatch({ type: "SET_UPDATE_USERNAME_LOADING", payload: false });
    }
  };
};

/**
 * Validates current password before allowing password change
 * This is a helper function to check if current password is correct
 */
export const validateCurrentPassword = (userEmail, currentPassword) => {
  return async (dispatch) => {
    try {
      dispatch({ type: "SET_VALIDATE_CURRENT_PASSWORD_LOADING", payload: true });

      // Use the login endpoint to validate the current password
      const response = await api.post('/login', {
        username: userEmail, // Use email as username for login validation
        password: currentPassword,
        mode: 'validateCurrentPassword'
      });

      if (response.status === 200) {
        dispatch({ type: "VALIDATE_CURRENT_PASSWORD_SUCCESS" });
        return { isValid: true };
      } else {
        dispatch({ type: "VALIDATE_CURRENT_PASSWORD_FAILURE", payload: 'Current password is incorrect' });
        return { isValid: false, error: 'Current password is incorrect' };
      }
    } catch (error) {
      // Handle different types of errors with user-friendly messages
      let errorMessage = 'Current password is incorrect';
      
      if (error?.response?.status === 401) {
        errorMessage = 'Current password is incorrect';
      } else if (error?.response?.status === 400) {
        errorMessage = 'Please enter a valid password';
      } else if (error?.response?.data?.message) {
        // If the error message contains "username", replace with "password"
        const originalMessage = error.response.data.message;
        if (originalMessage.toLowerCase().includes('username')) {
          errorMessage = 'Current password is incorrect';
        } else {
          errorMessage = originalMessage;
        }
      }
      
      dispatch({ type: "VALIDATE_CURRENT_PASSWORD_FAILURE", payload: errorMessage });
      return { isValid: false, error: errorMessage };
    } finally {
      dispatch({ type: "SET_VALIDATE_CURRENT_PASSWORD_LOADING", payload: false });
    }
  };
};

/**
 * Changes current user's password from My Profile
 * Backend: [POST] /user/{userId}/myprofile/change-password?currentPassword=...&newPassword=...
 */
export const changeMyProfilePassword = (userId, currentPassword, newPassword) => {
  return async (dispatch) => {
    try {
      dispatch({ type: "SET_CHANGE_MYPROFILE_PASSWORD_LOADING", payload: true });

      const url = `/user/${encodeURIComponent(userId)}/myprofile/change-password?currentPassword=${encodeURIComponent(currentPassword)}&newPassword=${encodeURIComponent(newPassword)}`;
      const response = await api.post(url);

      if (response.status === 200) {
        const msg = response.data?.message || 'Password changed successfully.';
        dispatch({ type: "CHANGE_MYPROFILE_PASSWORD_SUCCESS" });
        dispatch(displayToast(msg, 'success', { autoClose: 4000 }));
        // Optionally refresh user details to update passwordLastChangedOn dependent UI
        dispatch(getCurrentUserDetails(userId, false));
      } else {
        throw new Error(`Unexpected status ${response.status}`);
      }
    } catch (error) {
      // Extract error message from various possible error structures
      const errorMessage = error?.response?.data?.message || 
                          (typeof error?.response?.data === 'string' ? error.response.data : error?.response?.data?.error) ||
                          error?.message || 
                          'Failed to change password. Please try again.';
      
      const errorMessageLower = errorMessage.toLowerCase();
      const isPasswordHistoryError = ['passwordhistoryerror', 'previously used', 'previous', 'history', 'last five']
        .some(keyword => errorMessageLower.includes(keyword));
      
      dispatch({ type: "CHANGE_MYPROFILE_PASSWORD_FAILURE", payload: errorMessage });
      
      // Don't show toast for password history errors - the popup will handle it
      if (!isPasswordHistoryError) {
        dispatch(displayToast(normalizeErrorMessage(errorMessage), 'error', { autoClose: false }));
      }
    } finally {
      dispatch({ type: "SET_CHANGE_MYPROFILE_PASSWORD_LOADING", payload: false });
    }
  };
};

/**
 * Uploads user signature image
 * @param {string} userId - User ID to upload signature for
 * @param {File} signatureFile - Signature image file
 */
export const uploadSignature = (userId, signatureFile) => {
  return async (dispatch) => {
    try {
      dispatch({ type: "SET_SIGNATURE_UPLOAD_LOADING", payload: true });
      dispatch({ type: "CLEAR_SIGNATURE_UPLOAD_ERROR" });

      // Create FormData
      const formData = new FormData();

      // Add signature file
      formData.append("signatureImage", signatureFile);

      // Make API call
      const response = await api.post(`/user/${userId}/signature`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // Success handling
      dispatch({
        type: "UPLOAD_SIGNATURE_SUCCESS",
        payload: { userId, message: response.data.message }
      });

      dispatch(displayToast('Signature uploaded successfully!', 'success', { autoClose: 4000 }));
      dispatch(getCurrentUserDetails(userId, false));

    } catch (error) {
      console.error('Error uploading signature:', error);

      const errorMessage = error.response?.data?.message || error.message || 'Failed to upload signature';

      dispatch({ type: "UPLOAD_SIGNATURE_FAILURE", payload: errorMessage });
      dispatch(displayToast(normalizeErrorMessage(errorMessage), 'error', { autoClose: false }));
    } finally {
      dispatch({ type: "SET_SIGNATURE_UPLOAD_LOADING", payload: false });
    }
  };
};

/**
 * Deletes user signature
 * @param {string} userId - User ID to delete signature for
 */
export const deleteSignature = (userId) => {
  return async (dispatch) => {
    try {
      dispatch({ type: "SET_SIGNATURE_UPLOAD_LOADING", payload: true });
      dispatch({ type: "CLEAR_SIGNATURE_UPLOAD_ERROR" });

      // Make API call
      const response = await api.delete(`/user/${userId}/signature`);
      
      // Success handling
      dispatch({
        type: "DELETE_SIGNATURE_SUCCESS",
        payload: { userId, message: response.data.message }
      });

      dispatch(displayToast('Signature deleted successfully.', 'success', { autoClose: 4000 }));
      dispatch(getCurrentUserDetails(userId, false));

    } catch (error) {
      console.error('Error deleting signature:', error);

      const errorMessage = error.response?.data?.message || error.message || 'Failed to delete signature';

      dispatch({ type: "DELETE_SIGNATURE_FAILURE", payload: errorMessage });
      dispatch(displayToast(normalizeErrorMessage(errorMessage), 'error', { autoClose: false }));
    } finally {
      dispatch({ type: "SET_SIGNATURE_UPLOAD_LOADING", payload: false });
    }
  };
};

// System maintenance
export const checkSystemMaintenance = () => {
  return async (dispatch) => {
    const maintenanceApi = axios.create({
      baseURL: import.meta.env.VITE_APP_API_URL || 'http://localhost:62805',
    });

    try {
      const res = await maintenanceApi.get('/system-in-maintenance');

      const payload = res?.data;
      const isMaintenance =
        typeof payload === 'boolean'
          ? payload
          : typeof payload?.isSystemMaintenance === 'boolean'
            ? payload.isSystemMaintenance
            : Boolean(payload);

      dispatch({ type: 'SET_SYSTEM_MAINTENANCE', payload: isMaintenance });
    } catch (e) {
      // default to not showing maintenance page on failure
      dispatch({ type: 'SET_SYSTEM_MAINTENANCE', payload: false });
    }
  };
};